import React, { useEffect, useMemo, useState } from 'react';
import trainingApi from '../api';
import { nativeLabelFor } from '../i18n/strings';

const UserDetailView = () => {
  const [matrix, setMatrix] = useState(null);
  const [userId, setUserId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = await trainingApi.adminMetricsMatrix();
        setMatrix(data);
        if (data.users.length > 0) setUserId(String(data.users[0].id));
      } catch (e) {
        setError(e.message);
      }
    })();
  }, []);

  useEffect(() => {
    if (!userId) return;
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await trainingApi.adminUserDetail(userId);
        if (active) setRows(data);
      } catch (e) {
        if (active) setError(e.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [userId]);

  // Per-user timeline: sort most-recently-started first.
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => new Date(b.started_at) - new Date(a.started_at)),
    [rows]
  );

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-6">By User</h1>

      <div className="mb-4 flex items-center gap-2">
        <label className="text-sm text-slate-300">User:</label>
        <select
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          className="px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 min-w-[260px]"
        >
          {matrix?.users.map((u) => (
            <option key={u.id} value={u.id}>{u.full_name || u.username} ({u.username})</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-rose-900/40 border border-rose-700 text-rose-200">{error}</div>
      )}

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-slate-400">No attempts yet.</div>
      ) : (
        <div className="overflow-auto rounded-lg border border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-800 text-slate-300">
              <tr>
                <th className="text-left px-3 py-2">Training</th>
                <th className="text-left px-3 py-2">Language</th>
                <th className="text-left px-3 py-2">Started</th>
                <th className="text-left px-3 py-2">Completed</th>
                <th className="text-left px-3 py-2">Score</th>
                <th className="text-left px-3 py-2">Passed</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => (
                <tr key={r.id} className="border-t border-slate-700">
                  <td className="px-3 py-2 font-medium">{r.title}</td>
                  <td className="px-3 py-2 text-slate-300">{nativeLabelFor(r.language)}</td>
                  <td className="px-3 py-2 text-slate-400">{new Date(r.started_at).toLocaleString()}</td>
                  <td className="px-3 py-2 text-slate-400">
                    {r.completed_at ? new Date(r.completed_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {r.score_pct !== null ? `${Number(r.score_pct).toFixed(1)}%` : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {r.passed ? (
                      <span className="text-emerald-300">Yes</span>
                    ) : r.completed_at ? (
                      <span className="text-rose-300">No</span>
                    ) : (
                      <span className="text-slate-400">In progress</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserDetailView;
